import React from 'react'
import { Alert, Flex, Select, Skeleton, Switch, Space, Tooltip } from 'antd'
import { InfoCircleOutlined } from '@ant-design/icons'
import { Well } from '@pushly/aqe/lib/components'
import { useService } from '@pushly/aqe/lib/hooks'
import type { ExternalEventType } from '@pushly/models/lib/structs/external-events/external-event-type'
import type { ITriggerStep } from '@pushly/models/lib/structs/journeys/steps/step-interfaces'
import type { PQueryDocument } from '@pushly/models/lib/interfaces/pquery-document'
import { ExternalEventTypeService } from '../../../../services/external-event-type.service'
import { buildEventPropertyFields } from '../../external-events/event-property-fields'
import { EventConditionBuilder } from '../../external-events/event-condition-builder'
import {
    describeEventTypeSelection,
    findRegisteredEventType,
    formatEventTypeLabel,
} from '../../external-events/event-type-selection'

const EVENT_TYPE_ID = 'jc-external-event-type-select'
const EVENT_FILTER_ID = 'jc-external-event-filter-switch'

export interface IExternalEventSettingsSectionProps {
    domainId: number | undefined
    triggerConfig: ITriggerStep | undefined
    disabled?: boolean
    /** Same single-mutator apply the sibling sections use: the caller clones and commits. */
    onApply: (apply: (trigger: ITriggerStep | undefined) => void) => void
}

/**
 * Picks the event type a journey is triggered by, and authors the filter the event itself must
 * satisfy.
 *
 * Mounted explicitly rather than resolved from a registry — trigger-specific configuration is
 * mounted by the entry well beside the sections for the other trigger types.
 *
 * The event type is chosen from the domain's registry, which is also where the property map the
 * filter is written against comes from. A journey naming a type the registry no longer holds says
 * so here, where its author can see it: the stored name is kept and the reference is reported
 * rather than being quietly dropped to the first type that happens to be registered.
 */
export const ExternalEventSettingsSection: React.FC<IExternalEventSettingsSectionProps> = ({
    domainId,
    triggerConfig,
    disabled,
    onApply,
}) => {
    const eventTypeSvc = useService(ExternalEventTypeService)
    const [eventTypes, setEventTypes] = React.useState<ExternalEventType[]>([])
    const [loaded, setLoaded] = React.useState(false)

    React.useEffect(() => {
        let current = true
        if (domainId === undefined) return

        setLoaded(false)
        eventTypeSvc
            .list(domainId)
            .then((response) => {
                if (!current) return
                setEventTypes(response.ok && Array.isArray(response.data) ? response.data : [])
            })
            // A registry that could not be read leaves the surface saying so through the empty
            // select rather than through an unhandled rejection. The service has already reported
            // the failure itself.
            .catch(() => undefined)
            .finally(() => {
                if (current) setLoaded(true)
            })

        return () => {
            current = false
        }
    }, [eventTypeSvc, domainId])

    const params = triggerConfig?.configuration?.params
    const eventName = params?.event_name
    const schemaVersion = params?.schema_version
    const eventFilter = params?.event_filter

    const selected = findRegisteredEventType(eventTypes, eventName, schemaVersion)
    const selection = describeEventTypeSelection(loaded, eventTypes, eventName, schemaVersion)
    const propertyFields = React.useMemo(() => buildEventPropertyFields(selected?.properties), [selected])

    const handleEventTypeChange = (nextKey: string) => {
        const next = eventTypes.find((type) => formatEventTypeLabel(type).value === nextKey)
        onApply((trigger) => {
            if (!trigger?.configuration) return
            trigger.configuration.params = {
                ...trigger.configuration.params,
                event_name: next?.event_name,
                schema_version: next?.schema_version,
                // The filter is written against the chosen type's property map, so it cannot
                // survive a change of type: its conditions would name properties the new type does
                // not declare, and the router would evaluate them against nothing.
                event_filter: undefined,
            }
        })
    }

    const handleFilterEnabledChange = (enabled: boolean) => {
        onApply((trigger) => {
            if (!trigger?.configuration) return
            trigger.configuration.params = {
                ...trigger.configuration.params,
                // An empty AND group, not an empty object. The builder derives its root from the
                // document it is handed, so `{}` gives it no group to hold rules and it renders
                // nothing at all — the switch turns on and the reader is offered no way to filter.
                event_filter: enabled ? { and: [] } : undefined,
            }
        })
    }

    const handleFilterChange = (next: PQueryDocument) => {
        onApply((trigger) => {
            if (!trigger?.configuration) return
            trigger.configuration.params = { ...trigger.configuration.params, event_filter: next }
        })
    }

    return (
        <Well variant="ghost" title="External Event" footerMode="hidden" padding="none">
            <Skeleton loading={!loaded} active={true} title={false} paragraph={{ rows: 1, width: '100%' }}>
                <Flex vertical gap={8}>
                    <Flex align="center" gap={8}>
                        <label htmlFor={EVENT_TYPE_ID}>Event type</label>
                        <Select
                            id={EVENT_TYPE_ID}
                            size="small"
                            style={{ minWidth: 280 }}
                            disabled={disabled}
                            placeholder="Select a registered event type"
                            value={selected ? formatEventTypeLabel(selected).value : undefined}
                            onChange={handleEventTypeChange}
                            options={eventTypes.map(formatEventTypeLabel)}
                            // What a domain with no integration yet sees, and the only place it is
                            // told how one begins. An event type is not created by hand — it
                            // registers itself the first time a partner sends that event — so
                            // without saying so this reads as a surface that is simply broken.
                            notFoundContent="No event types yet. A type registers itself the first time a partner sends that event to the events API."
                            showSearch
                            optionFilterProp="label"
                        />
                    </Flex>

                    {selection.warning && <Alert type="warning" showIcon message={selection.warning} />}

                    {selected && (
                        <>
                            <Space>
                                <Switch
                                    id={EVENT_FILTER_ID}
                                    size="small"
                                    disabled={disabled}
                                    checked={!!eventFilter}
                                    onChange={handleFilterEnabledChange}
                                    aria-label="Restrict events that trigger this journey based on the event's properties"
                                />
                                <label htmlFor={EVENT_FILTER_ID}>
                                    Restrict events that trigger this journey based on the event's properties
                                </label>
                                {/*
                                  * Says which question this asks, because the audience well below asks a
                                  * different one that reads almost identically. This decides whether the
                                  * journey reacts to an event at all; that decides who enters when it does.
                                  */}
                                <Tooltip title="Checked against the event's own properties, before any audience is resolved. Events that do not match are ignored entirely.">
                                    <InfoCircleOutlined className="info-icon" aria-label="About restricting events" />
                                </Tooltip>
                            </Space>

                            {!!eventFilter && (
                                <EventConditionBuilder
                                    value={eventFilter}
                                    fields={propertyFields}
                                    disabled={disabled}
                                    onChange={handleFilterChange}
                                />
                            )}
                        </>
                    )}
                </Flex>
            </Skeleton>
        </Well>
    )
}

export default ExternalEventSettingsSection
